<template>
  <div
    class="ma-0 pa-0"
    style="width: 100%; height: 100%; margin: 0; border: 0; padding: 0"
  >
    <canvas
      v-if="suportWebGL"
      width="500"
      height="500"
      ref="canvas"
      style="width: 100%; height: 100%"
    ></canvas>
    <div v-else>
      <slot>
        Your browser does not seem to support
        <a
          href="http://khronos.org/webgl/wiki/Getting_a_WebGL_Implementation"
          style="color: #000"
          >WebGL</a
        >.<br />'
      </slot>
    </div>
  </div>
</template>

<script>
import {
  Object3D,
  Vector2,
  Vector3,
  Color,
  Clock,
  Scene,
  Raycaster,
  WebGLRenderer,
  PerspectiveCamera,
  AmbientLight,
  PointLight,
  HemisphereLight,
  DirectionalLight,
  LinearEncoding,
  //
  //PMREMGenerator,
  ACESFilmicToneMapping,
  sRGBEncoding,
  //AxesHelper,
  //LinearToneMapping,
  //ReinhardToneMapping,
  //NoToneMapping,
  //CineonToneMapping,
  //SpotLight,
  //UnsignedByteType,
  //AnimationMixer,
  //MeshBasicMaterial,
  //PlaneGeometry,
  //Mesh,
} from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { getSize, getCenter } from "./AugmentableUtils";

//import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader.js";

//import { HDRCubeTextureLoader } from "three/examples/jsm/loaders/HDRCubeTextureLoader.js";
//import { DebugEnvironment } from "three/examples/jsm/environments/DebugEnvironment.js";

import VueScreenSize from "vue-screen-size";

const suportWebGL = (() => {
  try {
    const canvas = document.createElement("canvas");
    return !!(
      window.WebGLRenderingContext &&
      (canvas.getContext("webgl") || canvas.getContext("experimental-webgl"))
    );
  } catch (e) {
    return false;
  }
})();
const DEFAULT_GL_OPTIONS = {
  antialias: true,
  alpha: true,
};
export default {
  props: {
    src: {
      type: String,
    },
    environmentImage: {
      type: String,
    },
    skyboxImage: {
      type: String,
    },
    exposure: {
      type: Number,
    },
    gltfLoader: {
      default: "anonymous",
    },
    manager: {
      default: "anonymous",
    },
    width: {
      type: Number,
    },
    height: {
      type: Number,
    },
    position: {
      type: Object,
      default() {
        return { x: 0, y: 0, z: 0 };
      },
    },
    rotation: {
      type: Object,
      default() {
        return { x: 0, y: 0, z: 0 };
      },
    },
    scale: {
      type: Object,
      default() {
        return { x: 1, y: 1, z: 1 };
      },
    },
    lights: {
      type: Array,
      default() {
        return [];
      },
    },
    cameraPosition: {
      type: Object,
      default() {
        return { x: 0, y: 0, z: 0 };
      },
    },
    cameraRotation: {
      type: Object,
      default() {
        return { x: 0, y: 0, z: 0 };
      },
    },
    cameraUp: {
      type: Object,
    },
    cameraLookAt: {
      type: Object,
    },
    backgroundColor: {
      default: 0xffffff,
    },
    backgroundAlpha: {
      type: Number,
      default: 1,
    },
    controlsOptions: {
      type: Object,
    },
    crossOrigin: {
      default: "anonymous",
    },
    requestHeader: {
      default: {},
    },
    outputEncoding: {
      type: Number,
      default: LinearEncoding,
    },
    glOptions: {
      type: Object,
    },
  },
  mixins: [VueScreenSize.VueScreenSizeMixin],
  data() {
    return {
      suportWebGL,
      size: {
        width: this.width,
        height: this.height,
      },
      object: null,
      raycaster: new Raycaster(),
      mouse: new Vector2(),
      camera: new PerspectiveCamera(45, 1, 0.9, 1000),
      //   camera: new PerspectiveCamera(
      //     45,
      //     window.innerWidth / window.innerHeight,
      //     1,
      //     10000
      //   ),

      scene: new Scene(),
      wrapper: new Object3D(),
      renderer: null,
      controls: null,
      allLights: [],
      clock: typeof performance === "undefined" ? Date : performance,
      reqId: null, // requestAnimationFrame id

      spotLight: null,

      modelLoad: null,
      animations: null,
      mixer: null,
      clock1: new Clock(),
    };
  },
  computed: {
    hasListener() {
      // 判断是否有鼠标事件监听，用于减少不必要的拾取判断
      /* eslint-disable no-underscore-dangle */
      const events = this._events;
      const result = {};
      ["on-mousemove", "on-mouseup", "on-mousedown", "on-click"].forEach(
        (name) => {
          result[name] = !!events[name] && events[name].length > 0;
        }
      );
      return result;
    },
  },
  mounted() {
    if (this.width === undefined || this.height === undefined) {
      this.size = {
        width: this.$el.offsetWidth,
        height: this.$el.offsetHeight,
      };
    }

    //added lately
    // this.camera = new PerspectiveCamera(
    //   45,
    //   this.$el.offsetWidth / this.$el.offsetHeight,
    //   1,
    //   1000
    // );
    //added lately

    const options = Object.assign({}, DEFAULT_GL_OPTIONS, this.glOptions, {
      canvas: this.$refs.canvas,
    });

    this.renderer = new WebGLRenderer(options);
    this.renderer.shadowMap.enabled = true;

    this.renderer.physicallyCorrectLights = true;
    //this.renderer.toneMapping = ACESFilmicToneMapping;

    this.renderer.outputEncoding = sRGBEncoding;

    // let hemiLight = new HemisphereLight(0xffffff, 0xffffff, 2);
    // this.scene.add(hemiLight);

    //this.spotLight = new SpotLight(0xffffff, 4);
    // this.spotLight.castShadow = true;
    // this.spotLight.shadow.bias = -0.0001;
    // this.spotLight.shadow.mapSize.width = 1024 * 4;
    // this.spotLight.shadow.mapSize.height = 1024 * 4;
    //this.scene.add(this.spotLight);

    //this.renderer.toneMapping = ReinhardToneMapping;
    this.renderer.toneMapping = ACESFilmicToneMapping;
    //this.renderer.toneMapping = NoToneMapping;
    //this.renderer.toneMapping = CineonToneMapping;
    //this.renderer.toneMapping = LinearToneMapping;

    //this.renderer.toneMappingExposure = 1;
    this.renderer.toneMappingExposure = parseFloat("0.7");

    this.renderer.shadowMap.enabled = true;

    // new RGBELoader()
    //   .setDataType(UnsignedByteType)
    //   .setPath("../assets/")
    //   .load("autoshop_01_1k.hdr", function (texture) {
    //     console.log("i am here!!!!!");
    //     console.log(texture);

    //     var envMap = pmremGenerator.fromEquirectangular(texture).texture;

    //     this.scene.background = envMap;
    //     this.scene.environment = envMap;

    //     texture.dispose();
    //     pmremGenerator.dispose();
    //   });
    // new RGBELoader()
    //   .setDataType(UnsignedByteType) // alt: FloatType, HalfFloatType
    //   .load("../assets/hdr.hdr", function (texture, textureData) {
    //     console.log("fffff");
    //     console.log(textureData);
    //     console.log(texture);

    //     const material = new MeshBasicMaterial({ map: texture });

    //     const quad = new PlaneGeometry(
    //       (1.5 * textureData.width) / textureData.height,
    //       1.5
    //     );

    //     const mesh = new Mesh(quad, material);

    //     this.scene.add(mesh);

    //     this.render();
    //   });
    //this.renderer

    // const pmremGenerator = new PMREMGenerator(renderer);
    // pmremGenerator.compileCubemapShader();

    // const envScene = new DebugEnvironment();
    // generatedCubeRenderTarget = pmremGenerator.fromScene(envScene);

    //this.renderer.outputEncoding = this.outputEncoding;

    this.controls = new OrbitControls(this.camera, this.$el);
    this.controls.type = "orbit";

    // this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    // this.controls.minDistance = 80;
    // this.controls.maxDistance = 300;

    // let hdrCubeMap = new HDRCubeTextureLoader()
    //   .setPath("../assets/")
    //   .setDataType(THREE.UnsignedByteType)
    //   .load("hdr.hdr", function () {
    //     hdrCubeRenderTarget = pmremGenerator.fromCubemap(hdrCubeMap);

    //     hdrCubeMap.magFilter = THREE.LinearFilter;
    //     hdrCubeMap.needsUpdate = true;
    //   });

    //this.scene.background = cubeMap;
    //this.scene.background = new Color(0xdddddd);

    //this.scene.add(new AxesHelper(500));

    this.scene.add(this.wrapper);
    this.load();
    this.changeEnvironmentImage();
    this.changeSkyboxImage();
    this.update();
    this.$el.addEventListener("mousedown", this.onMouseDown, false);
    this.$el.addEventListener("mousemove", this.onMouseMove, false);
    this.$el.addEventListener("mouseup", this.onMouseUp, false);
    this.$el.addEventListener("click", this.onClick, false);
    window.addEventListener("resize", this.onResize, false);
    this.animate();
  },
  beforeDestroy() {
    cancelAnimationFrame(this.reqId);
    this.scene.dispose();
    this.renderer.dispose();
    if (this.controls) {
      this.controls.dispose();
    }
    this.$el.removeEventListener("mousedown", this.onMouseDown, false);
    this.$el.removeEventListener("mousemove", this.onMouseMove, false);
    this.$el.removeEventListener("mouseup", this.onMouseUp, false);
    this.$el.removeEventListener("click", this.onClick, false);
    window.removeEventListener("resize", this.onResize, false);
  },
  watch: {
    src() {
      this.load();
    },
    environmentImage() {
      this.changeEnvironmentImage();
    },
    skyboxImage() {
      this.changeSkyboxImage();
    },
    exposure() {
      this.changeExposure();
    },
    rotation: {
      deep: true,
      handler(val) {
        if (!this.object) return;
        this.object.rotation.set(val.x, val.y, val.z);
      },
    },
    position: {
      deep: true,
      handler(val) {
        if (!this.object) return;
        this.object.position.set(val.x, val.y, val.z);
      },
    },
    scale: {
      deep: true,
      handler(val) {
        if (!this.object) return;
        this.object.scale.set(val.x, val.y, val.z);
      },
    },
    lights: {
      deep: true,
      handler() {
        this.updateLights();
      },
    },
    size: {
      deep: true,
      handler() {
        this.updateCamera();
        this.updateRenderer();
      },
    },
    controlsOptions: {
      deep: true,
      handler() {
        this.updateControls();
      },
    },
    backgroundAlpha() {
      this.updateRenderer();
    },
    backgroundColor() {
      this.updateRenderer();
    },
  },
  methods: {
    onResize() {
      if (this.width === undefined || this.height === undefined) {
        this.$nextTick(() => {
          this.size = {
            width: this.$el.offsetWidth,
            height: this.$el.offsetHeight,
          };
        });
      }
    },
    onMouseDown(event) {
      if (!this.hasListener["on-mousedown"]) return;
      const intersected = this.pick(event.clientX, event.clientY);
      this.$emit("on-mousedown", intersected);
    },
    onMouseMove(event) {
      if (!this.hasListener["on-mousemove"]) return;
      const intersected = this.pick(event.clientX, event.clientY);
      this.$emit("on-mousemove", intersected);
    },
    onMouseUp(event) {
      if (!this.hasListener["on-mouseup"]) return;
      const intersected = this.pick(event.clientX, event.clientY);
      this.$emit("on-mouseup", intersected);
    },
    onClick(event) {
      if (!this.hasListener["on-click"]) return;
      const intersected = this.pick(event.clientX, event.clientY);
      this.$emit("on-click", intersected);
    },
    pick(x, y) {
      if (!this.object) return null;
      const rect = this.$el.getBoundingClientRect();
      x -= rect.left;
      y -= rect.top;
      this.mouse.x = (x / this.size.width) * 2 - 1;
      this.mouse.y = -(y / this.size.height) * 2 + 1;
      this.raycaster.setFromCamera(this.mouse, this.camera);
      const intersects = this.raycaster.intersectObject(this.object, true);
      return (intersects && intersects.length) > 0 ? intersects[0] : null;
    },
    update() {
      this.updateRenderer();
      this.updateCamera();
      this.updateLights();
      this.updateControls();
    },
    updateModel() {
      const { object } = this;
      if (!object) return;
      const { position } = this;
      const { rotation } = this;
      const { scale } = this;
      object.position.set(position.x, position.y, position.z);
      object.rotation.set(rotation.x, rotation.y, rotation.z);
      object.scale.set(scale.x, scale.y, scale.z);
    },
    updateRenderer() {
      const { renderer } = this;
      renderer.setSize(this.size.width, this.size.height);
      renderer.setPixelRatio(window.devicePixelRatio || 1);
      renderer.setClearColor(new Color(this.backgroundColor).getHex());
      renderer.setClearAlpha(this.backgroundAlpha);
    },
    updateCamera() {
      const { camera } = this;
      const { object } = this;
      camera.aspect = this.size.width / this.size.height;
      camera.updateProjectionMatrix();
      if (!this.cameraLookAt && !this.cameraUp) {
        if (!object) return;
        const distance = getSize(object).length();
        camera.position.set(
          this.cameraPosition.x,
          this.cameraPosition.y,
          this.cameraPosition.z
        );
        camera.rotation.set(
          this.cameraRotation.x,
          this.cameraRotation.y,
          this.cameraRotation.z
        );
        if (
          this.cameraPosition.x === 0 &&
          this.cameraPosition.y === 0 &&
          this.cameraPosition.z === 0
        ) {
          camera.position.z = distance;
        }
        camera.lookAt(new Vector3());
      } else {
        camera.position.set(
          this.cameraPosition.x,
          this.cameraPosition.y,
          this.cameraPosition.z
        );
        camera.rotation.set(
          this.cameraRotation.x,
          this.cameraRotation.y,
          this.cameraRotation.z
        );
        camera.up.set(this.cameraUp.x, this.cameraUp.y, this.cameraUp.z);
        camera.lookAt(
          new Vector3(
            this.cameraLookAt.x,
            this.cameraLookAt.y,
            this.cameraLookAt.z
          )
        );
      }
    },
    updateLights() {
      this.scene.remove(...this.allLights);
      this.allLights = [];
      this.lights.forEach((item) => {
        if (!item.type) return;
        const type = item.type.toLowerCase();
        let light = null;
        if (type === "ambient" || type === "ambientlight") {
          const color =
            item.color === 0x000000 ? item.color : item.color || 0x404040;
          const intensity =
            item.intensity === 0 ? item.intensity : item.intensity || 1;
          light = new AmbientLight(color, intensity);
        } else if (type === "point" || type === "pointlight") {
          const color =
            item.color === 0x000000 ? item.color : item.color || 0xffffff;
          const intensity =
            item.intensity === 0 ? item.intensity : item.intensity || 1;
          const distance = item.distance || 0;
          const decay = item.decay === 0 ? item.decay : item.decay || 1;
          light = new PointLight(color, intensity, distance, decay);
          if (item.position) {
            light.position.copy(item.position);
          }
        } else if (type === "directional" || type === "directionallight") {
          const color =
            item.color === 0x000000 ? item.color : item.color || 0xffffff;
          const intensity =
            item.intensity === 0 ? item.intensity : item.intensity || 1;
          light = new DirectionalLight(color, intensity);
          if (item.position) {
            light.position.copy(item.position);
          }
          if (item.target) {
            light.target.copy(item.target);
          }
        } else if (type === "hemisphere" || type === "hemispherelight") {
          const skyColor =
            item.skyColor === 0x000000
              ? item.skyColor
              : item.skyColor || 0xffffff;
          const groundColor =
            item.groundColor === 0x000000
              ? item.groundColor
              : item.groundColor || 0xffffff;
          const intensity =
            item.intensity === 0 ? item.intensity : item.intensity || 1;
          light = new HemisphereLight(skyColor, groundColor, intensity);
          //   light = new HemisphereLight(0xffffff, 0x000000, 1);
          //   alert("ggg");
          //   alert(light);
          if (item.position) {
            light.position.copy(item.position);
          }
        }
        this.allLights.push(light);
        this.scene.add(light);
      });
    },
    updateControls() {
      if (this.controlsOptions) {
        Object.assign(this.controls, this.controlsOptions);
      }
    },
    load() {
      if (!this.src) return;
      if (this.object) {
        this.wrapper.remove(this.object);
      }

      this.loader.setRequestHeader(this.requestHeader);

      this.loader.load(
        this.src,
        (...args) => {
          const object = this.getObject(...args);
          if (this.process) {
            this.process(object);
          }
          this.addObject(object);
          this.$emit("on-load");
        },
        (xhr) => {
          this.$emit("on-progress", xhr);
        },
        (err) => {
          this.$emit("on-error", err);
        }
      );
    },
    // changeEnvironmentImage() {
    //   if (!this.src) return;

    //   let { scene } = this;

    //   const pmremGenerator = new PMREMGenerator(this.renderer);
    //   pmremGenerator.compileEquirectangularShader();
    //   new RGBELoader()
    //     .setDataType(UnsignedByteType)
    //     .setPath("../assets/")
    //     .load("HDRLabs_FactoryPumps_grey.hdr", function (texture) {
    //       console.log("i am here!!!!!");
    //       console.log(texture);

    //       var envMap = pmremGenerator.fromEquirectangular(texture).texture;
    //       //console.log(envMap);
    //       //console.log(this.renderer);
    //       scene.background = envMap;
    //       scene.environment = envMap;

    //       texture.dispose();
    //       pmremGenerator.dispose();
    //     });
    // },
    getObject(object) {
      return object;
    },
    addObject(object) {
      const center = getCenter(object);
      // correction position
      this.wrapper.position.copy(center.negate());
      this.object = object;
      this.wrapper.add(object);
      this.updateCamera();
      this.updateModel();
    },
    animate() {
      this.reqId = requestAnimationFrame(this.animate);
      this.render();
    },
    render() {
      if (this.mixer) this.mixer.update(this.clock1.getDelta());
      this.renderer.render(this.scene, this.camera);
      //this one was working before
      // this.spotLight.position.set(
      //   this.camera.position.x + 10,
      //   this.camera.position.y + 10,
      //   this.camera.position.z + 10
      // );
    },
  },
};
</script>